import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, FormControlLabel, Switch, Typography} from '@mui/material'
import {Controller, useFormContext} from 'react-hook-form'
import RHFSearchableSelectField from '../../common/hook-form/RHFSearchableSelectField'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE, BORDER} from '../../styles/foundation/colors'

// Form field names owned by this component. Callers must strip them from the request they send
// (see `RTO_FORM_FIELDS`) and add `return_details` instead (see `buildReturnDetails`).
export const RTO_FORM_FIELDS = ['useDifferentRto', 'rtoLocation']

export const RTO_DEFAULT_VALUES = {useDifferentRto: false, rtoLocation: ''}

const addressLabel = (location) =>
  [location.name, location.address_line, location.city, location.state, location.pincode]
    .filter(Boolean)
    .join(', ')

// Yup rule for the selected RTO address; add it to the form schema as `rtoLocation`.
export const rtoLocationRule = (Yup) =>
  Yup.string().when('useDifferentRto', {
    is: (enabled) => !!enabled,
    then: Yup.string().required('Please select the RTO address.'),
  })

// The optional RTO address for the create-shipment request. Returns undefined unless the merchant
// asked for a different RTO address, so the request stays exactly as before in every other case.
export function buildReturnDetails(formValues, locations) {
  if (!formValues?.useDifferentRto) return undefined
  const location = (locations || []).find((l) => l.id === formValues.rtoLocation)
  if (!location) return undefined
  return {
    name: location.name,
    phone_number: location.contactNumber,
    address: location.address_line,
    city: location.city,
    state: location.state,
    pincode: location.pincode,
    country: location.country,
    email: location.email,
    gstin: location.gstin,
  }
}

// "Select different RTO address" toggle for the create-shipment screens. Must be rendered inside a
// react-hook-form provider. `locations` are the merchant's active pickup locations; the location
// already chosen as pickup (`pickupLocationId`) is left out of the list.
function RtoAddressSelector({locations, pickupLocationId}) {
  const {control, watch, setValue, clearErrors} = useFormContext()
  const enabled = !!watch('useDifferentRto')
  const rtoLocation = watch('rtoLocation')

  // The "select an RTO address" error is stale as soon as one is chosen.
  useEffect(() => {
    if (rtoLocation) clearErrors('rtoLocation')
  }, [rtoLocation, clearErrors])

  // Pickup changed to the address already picked as RTO: that choice is no longer "different".
  useEffect(() => {
    if (rtoLocation && rtoLocation === pickupLocationId) setValue('rtoLocation', '')
  }, [rtoLocation, pickupLocationId, setValue])

  const options = (locations || [])
    .filter((l) => l.id !== pickupLocationId)
    .map((l) => ({value: l.id, label: addressLabel(l)}))

  const onToggle = (checked, fieldOnChange) => {
    fieldOnChange(checked)
    if (!checked) setValue('rtoLocation', '')
  }

  return (
    <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', px: 1.5, py: 1}}>
      <Controller
        name='useDifferentRto'
        control={control}
        render={({field}) => (
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={!!field.value}
                onChange={(e) => onToggle(e.target.checked, field.onChange)}
              />
            }
            label={
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.medium, color: NAVY}}>
                Select different RTO address
              </Typography>
            }
          />
        )}
      />
      <Typography sx={{fontFamily: FONT, fontSize: 11, color: SUBTLE, ml: 0.5}}>
        By default undelivered shipments return to the pickup address. Turn this on to send them to another address.
      </Typography>

      {enabled && (
        <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1}}>
          <RHFSearchableSelectField
            name='rtoLocation'
            label='Select RTO address'
            width='100%'
            options={options}
            noOptionsText='No other address available'
          />
          <Typography sx={{fontFamily: FONT, fontSize: 12, color: SUBTLE}}>
            RTO address not listed?{' '}
            <a
              href='/merchant-step2'
              target='_blank'
              rel='noopener noreferrer'
              style={{color: PRIMARY_BLUE, textDecoration: 'none', fontWeight: 500}}
            >
              Add a new address
            </a>
          </Typography>
        </Box>
      )}
    </Box>
  )
}

RtoAddressSelector.propTypes = {
  locations: PropTypes.array,
  pickupLocationId: PropTypes.string,
}

export default RtoAddressSelector
